import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import usePoints from "./usePoints";
import { Vector3, QuadraticBezierCurve3 } from "three";
import {
  setHiglightPoint,
  setRedoStack,
  setShowSnapLine,
  setSnappingPoint,
  setUndoStack,
  setTypeId,
} from "../Actions/DrawingActions";
import { findLineForPoint } from "../utils/coolinear.js";
import convert from "convert-units";
import { calculateAlignedPoint } from "../utils/uniqueId";
import {
  setPoints,
  setSelectedLinesState,
  setStoreLines,
} from "../Actions/ApplicationStateAction.js";
import _ from "lodash";
import { getSnappedPoint } from "../utils/snapping.js";
import { current } from "@reduxjs/toolkit";
import { findHoveredNovaLine } from "../utils/lineDetection.js";

const useMouse = () => {
  const dispatch = useDispatch();
  const { screenToNDC } = usePoints();
  const { perpendicularLine, measured, roomSelectors, lineBreak, snapActive, merge } =
    useSelector((state) => state.drawing);
  const { dragMode, enablePolygonSelection, actionHistory } = useSelector(
    (state) => state.Drawing
  );
  const {
    storeLines,
    points,
    factor,
    selectionMode,
    selectedLines,
    expandRoomPopup,
    designStep,
    activeRoomButton,
    tutorialActive,
    autoDraw,
    novaPoints,
    novaLines,
    img,
  } = useSelector((state) => state.ApplicationState);

  const [isSelecting, setIsSelecting] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [endPoint, setEndPoint] = useState(null);
  const [nearPoint, setNearPoint] = useState(false);
  const [nearVal, setNearVal] = useState();
  const [draggingPointIndex, setDraggingPointIndex] = useState(null);
  const [draggingLineIndex, setDraggingLineIndex] = useState([]);
  const [draggingLine, setDraggingLine] = useState(null);
  const [currentLinePostion, setCurrentLinePostion] = useState(null);
  const [currentMousePosition, setCurrentMousePosition] = useState(null);
  const [currentStrightMousePosition, setCurrentStrightMousePosition] =
    useState(null);
  const [distance, setDistance] = useState(null);
  const [curvePoints, setCurvePoints] = useState(null);
  const [curveAngle, setCurveAngle] = useState(0);
  const [curveAnglePosition, setCurveAnglePosition] = useState(null);
  const [lineAngle, setLineAngle] = useState(null);

  const xMap = new Map();
  const yMap = new Map();

  points.forEach((point) => {
    const xKey = point.x;
    const yKey = point.y;
    xMap.set(xKey, point);
    yMap.set(yKey, point);
  });

  const findInMap = (map, callback) => {
    let found;
    map.forEach((value, key) => {
      if (callback(value, key)) {
        found = { key, value };
      }
    });
    return found;
  };

  let scheduled = false;

  const snapToPoint = (cuuPoint, lastPoint) => {
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        const { snappedPoint, snapPoints } = getSnappedPoint(cuuPoint, lastPoint, points, snapActive);
        if (snapPoints.length > 0) {
          dispatch(setSnappingPoint([...snapPoints, snappedPoint]));
          dispatch(setShowSnapLine(true));
        } else {
          dispatch(setSnappingPoint([]));
          dispatch(setShowSnapLine(false));
        }
      });
    }
  };

  const controlPoint = (point1, point2, angle = 0) => {
    let x = (point1?.x + point2?.x) / 2;
    let y = (point1?.y + point2?.y) / 2;
    if (Math.abs(point1?.x - point2?.x) < Math.abs(point1?.y - point2?.y)) {
      if (points[points.length - 1].x > point2?.x) {
        x = x - angle * 0.67;
        setCurveAnglePosition(new Vector3(x - 20, y, 0));
      } else {
        x = x + angle * 0.67;
        setCurveAnglePosition(new Vector3(x + 20, y, 0));
      }
    } else {
      if (points[points.length - 1].y > point2?.y) {
        y = y - angle * 0.67;
        setCurveAnglePosition(new Vector3(x, y - 20, 0));
      } else {
        y = y + angle * 0.67;
        setCurveAnglePosition(new Vector3(x, y + 20, 0));
      }
    }
    return new Vector3(x, y, 0);
  };

  const handleMouseMove = (event) => {
    let point = screenToNDC(event.clientX, event.clientY);

    if (designStep === 3 && activeRoomButton === "divide") {
      setCurrentMousePosition(point);
      return;
    }

    // Calculate aligned point first if perpendicularLine is active
    if (designStep !== 3 && perpendicularLine && !draggingPointIndex && points.length > 0) {
      point = calculateAlignedPoint(points[points.length - 1], point);
    }

    // Compute snapped position based on the aligned or raw point
    const lastPoint = points.length > 0 ? points[points.length - 1] : null;
    const { snappedPoint } = getSnappedPoint(point, lastPoint, points, snapActive);
    point = snappedPoint;

    if (designStep === 3 && enablePolygonSelection) {
      setCurrentMousePosition(point);
      let newarr = [];
      let snapFound = false;

      roomSelectors.forEach((room) => {
        const points = room.polygon;
        for (let i = 0; i < points.length; i++) {
          if (Math.abs(points[i].x - point.x) < 2) {
            point.x = points[i].x;
            newarr = [point, new Vector3(points[i].x, points[i].y, 0)];
            snapFound = true;
            break;
          } else if (Math.abs(points[i].y - point.y) < 2) {
            point.y = points[i].y;
            newarr = [point, new Vector3(points[i].x, points[i].y, 0)];
            snapFound = true;
            break;
          }
        }
      });

      dispatch(setShowSnapLine(snapFound));
      dispatch(setSnappingPoint(snapFound ? newarr : []));

      if (!isSelecting) return;
      setEndPoint(point);
    }

    if (selectionMode && designStep === 2) {
      setCurrentMousePosition(point);
    }

    if (points.length === 0 || (designStep === 1 && !tutorialActive) || selectionMode) return;

    if (designStep === 3) return;

    if (lineBreak) {
      const { closestPointOnLine, line } = findLineForPoint(point, storeLines, snapActive);
      if (closestPointOnLine) {
        setNearPoint(true);
        setNearVal({ line, point: closestPointOnLine });
      } else {
        setNearPoint(false);
        setNearVal(null);
      }
    }

    if (lastPoint) {
      snapToPoint(point, lastPoint);
    }

    if (perpendicularLine && !draggingPointIndex && points.length > 0) {
      point = calculateAlignedPoint(points[points.length - 1], point);
    }

    if (!perpendicularLine && !draggingPointIndex && points.length > 0) {
      const lastPoint = points[points.length - 1];
      const position = calculateAlignedPoint(lastPoint, point);
      const V1 = new Vector3().subVectors(position, lastPoint);
      const V2 = new Vector3().subVectors(point, lastPoint);
      const angleInDegrees = V1.angleTo(V2) * (180 / Math.PI);

      setCurveAngle(angleInDegrees < 3 ? 0 : angleInDegrees);
      setLineAngle(Math.atan2(position.y - lastPoint.y, position.x - lastPoint.x));

      const curve = new QuadraticBezierCurve3(
        position,
        controlPoint(position, point, angleInDegrees),
        point
      );
      setCurvePoints(curve.getPoints(100));
      setCurrentStrightMousePosition(position);
      setDistance(lastPoint.distanceTo(position) * factor[0]);
    }

    // Auto-update line type when hovering over nova lines (autoDraw mode)
    // if (autoDraw && img && novaLines && novaLines.length > 0 && points.length > 0 && !selectionMode) {
    //   const hoveredLine = findHoveredNovaLine(point, novaLines, 15);
    //   if (hoveredLine && hoveredLine.typeId) {
    //     dispatch(setTypeId(hoveredLine.typeId));
    //   }
    // }

    setCurrentMousePosition(point);
  };

  const handleMouseDown = (event) => {
    if (!dragMode || merge || lineBreak) return;

    let rawPoint = screenToNDC(event.clientX, event.clientY);

    if (perpendicularLine && points.length > 0) {
      rawPoint = calculateAlignedPoint(points[points.length - 1], rawPoint);
    }

    const lastPoint = points.length > 0 ? points[points.length - 1] : null;
    const { snappedPoint } = getSnappedPoint(rawPoint, lastPoint, points, snapActive);
    const point = snappedPoint;

    if (designStep === 2) {
      console.log("points", points)
      const pointIndex = points.findIndex((p) => p.distanceTo(point) < 10);
      if (pointIndex !== -1) {
        setDraggingPointIndex(pointIndex);
        const beforeUpdation = points[pointIndex];

        const updatedDraggingLineIndex = storeLines.reduce((acc, line, index) => {
          if (line.points[0].equals(beforeUpdation)) {
            acc.push({ index, type: "start" });
          }
          if (line.points[1].equals(beforeUpdation)) {
            acc.push({ index, type: "end" });
          }
          return acc;
        }, []);

        setDraggingLineIndex(updatedDraggingLineIndex);
      } else if (selectedLines.length > 0) {
        const lineIndex = storeLines.findIndex(
          (line) => line.id === selectedLines[0]
        );
        if (lineIndex !== -1) {
          const line = storeLines[lineIndex];
          const val =
            Math.abs(line.points[0].y - point.y) < 10 ||
            Math.abs(line.points[1].y - point.y) < 10 ||
            Math.abs(line.points[0].x - point.x) < 10 ||
            Math.abs(line.points[1].x - point.x) < 10;
          if (val) setDraggingLine(lineIndex);
        }
      }
    }
  };

  const handleMouseUp = (event) => {
    if ((draggingPointIndex !== null || draggingLine !== null) && designStep === 2) {
      let rawPoint = screenToNDC(event.clientX, event.clientY);

      if (perpendicularLine && points.length > 0) {
        rawPoint = calculateAlignedPoint(points[points.length - 1], rawPoint);
      }

      const lastPoint = points.length > 0 ? points[points.length - 1] : null;
      const { snappedPoint } = getSnappedPoint(rawPoint, lastPoint, points, snapActive);
      let point = snappedPoint;

      const history = [...actionHistory];
      const pts = storeLines
        .flatMap((line) => line.points)
        .filter((v, i, a) => a.findIndex((t) => t.x === v.x && t.y === v.y) === i);
      
      // console.log("draggingPointIndex, draggingLine, currentLinePostion", draggingPointIndex, draggingLine, currentLinePostion)
      if (draggingPointIndex !== null) {
        const beforeUpdation = points[draggingPointIndex];

        pts.forEach((pt) => {
          if (point.distanceTo(pt) < 10 && !pt.equals(beforeUpdation)) {
            point = pt;
          }
        });

        const updatedPoints = points.map((p) => (p.equals(beforeUpdation) ? point : p));

        const updatedLines = storeLines.map((line) => {
          if (line.points[0].equals(beforeUpdation)) {
            return {
              ...line,
              points: [point, line.points[1]],
              length: convert(point.distanceTo(line.points[1]) * factor[0])
                .from(measured)
                .to("mm"),
            };
          }
          if (line.points[1].equals(beforeUpdation)) {
            return {
              ...line,
              points: [line.points[0], point],
              length: convert(line.points[0].distanceTo(point) * factor[0])
                .from(measured)
                .to("mm"),
            };
          }
          return line;
        });

        dispatch(setPoints(updatedPoints));
        dispatch(setStoreLines(updatedLines));
        history.push({
          type: "pointLineDrag",
          prevPoints: points,
          prevLines: storeLines,
          updatedPoints,
          updatedLines,
        });
      }

      if (draggingLine !== null && currentLinePostion) {
        let [startPoint, endPoint] = currentLinePostion;

        pts.forEach((pt) => {
          if (startPoint.distanceTo(pt) < 10) startPoint = pt;
          if (endPoint.distanceTo(pt) < 10) endPoint = pt;
        });

        const updatedPoints = points.map((p) => {
          if (p.equals(storeLines[draggingLine].points[0])) return startPoint;
          if (p.equals(storeLines[draggingLine].points[1])) return endPoint;
          return p;
        });

        const updatedLines = storeLines.map((line, index) =>
          index === draggingLine
            ? {
                ...line,
                points: [startPoint, endPoint],
                length: convert(startPoint.distanceTo(endPoint) * factor[0])
                  .from(measured)
                  .to("mm"),
              }
            : line
        );

        dispatch(setPoints(updatedPoints));
        dispatch(setStoreLines(updatedLines));
        history.push({
          type: "pointLineDrag",
          prevPoints: points,
          prevLines: storeLines,
          updatedPoints,
          updatedLines,
        });
      }

      dispatch(setUndoStack(history));
      dispatch(setRedoStack([]));
    }

    setDraggingPointIndex(null);
    setDraggingLineIndex([]);
    setCurrentLinePostion(null);
    setDraggingLine(null);

    if (designStep === 3 && expandRoomPopup && isSelecting) {
      const selectedIds = storeLines
        .filter((line) => {
          const [sp, ep] = line.points;
          return (
            sp.x >= Math.min(startPoint.x, endPoint.x) &&
            sp.x <= Math.max(startPoint.x, endPoint.x) &&
            sp.y >= Math.min(startPoint.y, endPoint.y) &&
            sp.y <= Math.max(startPoint.y, endPoint.y) &&
            ep.x >= Math.min(startPoint.x, endPoint.x) &&
            ep.x <= Math.max(startPoint.x, endPoint.x) &&
            ep.y >= Math.min(startPoint.y, endPoint.y) &&
            ep.y <= Math.max(startPoint.y, endPoint.y)
          );
        })
        .map((line) => line.id);

      dispatch(setSelectedLinesState(selectedIds));
      setIsSelecting(false);
      setStartPoint(null);
      setEndPoint(null);
    }
  };

  return {
    handleMouseMove,
    handleMouseUp,
    handleMouseDown,
    isSelecting,
    startPoint,
    endPoint,
    nearPoint,
    nearVal,
    draggingLineIndex,
    draggingLine,
    currentLinePostion,
    setCurrentLinePostion,
    currentMousePosition,
    currentStrightMousePosition,
    distance,
    curvePoints,
    curveAngle,
    curveAnglePosition,
    lineAngle,
  };
};

export default useMouse;
